<template>
  <div class="detail-container">
    <!-- 面包屑与头部操作区 -->
    <div class="detail-header">
      <div class="header-left">
        <el-breadcrumb separator="/">
          <el-breadcrumb-item>{{ $t('fixDutyFine.penaltyStatementBreadcrumb') }}
          </el-breadcrumb-item>
        </el-breadcrumb>
        <h2 class="page-title">{{ routeInfo.type }} - {{ $t('fixDutyFine.waybillDetail') }}</h2>
        <p class="sub-title">
          {{ routeInfo.reconciliationPeriod }} / {{ routeInfo.regionName }} / {{ routeInfo.type }}
        </p>
      </div>
      <div class="header-right">
        <el-button size="small" @click="handleBack">{{ $t('fixDutyFine.backToSummary') }}
        </el-button>
        <el-button size="small" type="primary" @click="exportData">{{ $t('fixDutyFine.exportDetail') }}
        </el-button>
      </div>
    </div>

    <!-- 状态统计区 (类似 Tab) -->
    <div class="status-tabs">
      <div
        v-for="(tab, index) in statusTabs"
        :key="index"
        :class="['tab-item', { active: query.statusList === tab.value }]"
        @click="handleTabClick(tab.value)"
      >
        {{ tab.label }}
        <span :class="['count', tab.colorClass]">{{ formatNumber(tab.count) }}</span>
      </div>
    </div>

    <!-- 筛选区 -->
    <div class="filter-area">
      <el-form :inline="true" size="small">
        <el-form-item>
          <el-input
            v-model="query.waybillNumber"
            :placeholder="$t('fixDutyFine.searchWaybill')"
            clearable
            style="width: 250px"
          />
        </el-form-item>
        <el-form-item>
          <el-select
            v-model="query.responsibilityTypeList"
            :placeholder="$t('fixDutyFine.allPenaltyTypes')"
            clearable
            collapse-tags
            multiple
            style="width: 220px"
          >
            <el-option
              v-for="item in responsibilityTypeList"
              :key="item.key"
              :label="item.value"
              :value="item.key"
            />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-select
            v-model="query.regionId"
            :placeholder="$t('fixDutyFine.allRegions')"
            clearable
            collapse-tags
            multiple
            style="width: 220px"
          >
            <el-option
              v-for="item in regionsList"
              :key="item.id"
              :label="getRegionName(item)"
              :value="item.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-select
            v-model="query.statusList"
            :placeholder="$t('fixDutyFine.allProcessStatus')"
            style="width: 150px"
          >
            <el-option
              v-for="item in statusOptions"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button icon="el-icon-search" type="primary" @click="handleQuery">{{ $t('fixDutyFine.query') }}
          </el-button>
          <el-button icon="el-icon-refresh" @click="resetQuery">{{ $t('fixDutyFine.reset') }}
          </el-button>
        </el-form-item>
      </el-form>

      <div class="batch-actions">
        <span class="hint-text">{{
          $t('fixDutyFine.selectedHint', { count: selectedRows.length })
        }}</span>
        <el-button plain size="small" @click="exportData">{{ $t('fixDutyFine.export') }}</el-button>
        <el-button
          v-if="hasPermit('btn:DeductionConfirmationForm:batchProcess')"
          size="small"
          type="primary"
          @click="handleBatchProcess"
        >{{ $t('fixDutyFine.batchProcess') }}
        </el-button>
        <el-button
          v-if="hasPermit('btn:DeductionConfirmationForm:batchFinalDetermination')"
          size="small"
          type="primary"
          @click="handleBatchFinalDetermination"
        >
          {{ $t('fixDutyFine.batchFinalDetermination') }}
        </el-button>
      </div>
    </div>

    <!-- 明细表格 -->
    <el-table
      :data="tableData"
      border
      style="width: 100%"
      @selection-change="handleSelectionChange"
    >
      <el-table-column align="center" type="selection" width="55" />
      <el-table-column
        :label="$t('base.billingMonth')"
        align="center"
        prop="reconciliationPeriod"
        width="100"
      />
      <el-table-column
        :label="$t('fixDutyFine.waybillNumber')"
        align="center"
        prop="waybillNumber"
        width="160"
      >
        <template slot-scope="scope">
          <span class="bold-waybill clickable-waybill" @click="handleAction(scope.row, 'view')">{{
            scope.row.waybillNumber
          }}</span>
        </template>
      </el-table-column>
      <el-table-column
        :label="$t('fixDutyFine.region')"
        align="center"
        prop="regionName"
        width="90"
      >
        <template slot-scope="scope">
          {{ formatRegionName(scope.row.regionName) }}
        </template>
      </el-table-column>
      <el-table-column
        :label="$t('fixDutyFine.site')" align="center" prop="siteName"
        width="100"
      />
      <el-table-column
        :label="$t('fixDutyFine.latestTrackContent')"
        align="left"
        prop="latestTrackContent"
        show-overflow-tooltip
      />
      <el-table-column
        :label="$t('fixDutyFine.latestTrackTime')"
        align="center"
        prop="latestTrackTime"
        width="150"
      />
      <el-table-column
        :label="$t('fixDutyFine.penaltyType')"
        align="center"
        prop="responsibilityTypeName"
        width="100"
      />
      <el-table-column
        :label="$t('fixDutyFine.DefiniteTime')"
        align="center"
        prop="responsibilityTime"
        width="100"
      />
      <el-table-column
        :label="$t('fixDutyFine.penaltyAmount')"
        align="center"
        prop="totalPenaltyAmount"
        width="100"
      >
        <template slot-scope="scope">{{ formatCurrency(scope.row.totalPenaltyAmount) }}</template>
      </el-table-column>
      <el-table-column
        :label="$t('fixDutyFine.processStatus')"
        align="center"
        prop="statusName"
        width="120"
      >
        <template slot-scope="scope">
          <!-- 用 span 包裹状态，动态绑定 class，并处理换行 -->
          <span :class="getStatusClass(scope.row.status)" class="status-pill">
            {{ scope.row.statusName }}
          </span>
        </template>
      </el-table-column>
      <el-table-column
        :label="$t('fixDutyFine.appealReason')"
        align="left"
        prop="appealReason"
        show-overflow-tooltip
      />
      <el-table-column
        :label="$t('fixDutyFine.finalReviewOpinion')"
        align="left"
        prop="finalReviewOpinion"
        show-overflow-tooltip
      />
      <!-- 操作列 -->
      <el-table-column
        :label="$t('fixDutyFine.operation')"
        align="center"
        fixed="right"
        width="170"
      >
        <template slot-scope="scope">
          <!-- 1. 仅「待处理」状态（status===1）且有「处理」权限时，展示「处理」按钮 -->
          <el-button
            v-if="scope.row.status === 1 && hasPermit('btn:DeductionConfirmationForm:process')"
            size="mini"
            type="primary"
            @click="handleAction(scope.row, 'process')"
          >{{ $t('fixDutyFine.process') }}
          </el-button>

          <!-- 2. 仅「待复核」状态（status===3）且有「最终定责」权限时，展示「最终定责」按钮 -->
          <el-button
            v-if="
              scope.row.status === 3 &&
                hasPermit('btn:DeductionConfirmationForm:finalDetermination')
            "
            size="mini"
            type="primary"
            @click="handleAction(scope.row, 'final')"
          >{{ $t('fixDutyFine.finalDetermination') }}
          </el-button>

          <!-- 3. 所有状态都展示「查看」按钮 -->
          <el-button plain size="mini" @click="handleAction(scope.row, 'view')">{{ $t('fixDutyFine.view') }}
          </el-button>
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页 -->
    <div class="pagination-container">
      <div class="pagination-left">{{ $t('fixDutyFine.paginationTip') }}</div>
      <el-pagination
        :current-page.sync="query.page"
        :page-size.sync="query.size"
        :page-sizes="[10, 20, 50, 100]"
        :total="total"
        background
        layout="total, sizes, prev, pager, next, jumper"
        @current-change="getCurrentTableData"
        @size-change="getSizeTableData"
      />
    </div>
    <!-- 使用 ref 引用子组件，不再传大量 props -->
    <recordDrawer ref="drawer" />
    <process-penalty-dialog ref="processDialog" @submit-success="handleProcessSuccess" />
    <!-- 新增：底部引入组件 -->
    <final-determination-dialog ref="finalDialog" @submit-success="handleFinalSuccess" />
  </div>
</template>

<script>
import recordDrawer from './recordDrawer.vue'; // 引入组件
import ProcessPenaltyDialog from './ProcessPenaltyDialog.vue';
import FinalDeterminationDialog from './FinalDeterminationDialog.vue';
import {
  detailSummary,
  getDetailList,
  getDetailListExport
} from '@/api/definiteResponsibilityAndPenalty';
import { goRWExportList } from '@/utils/goExportList'; // 引入新组件
export default {
  name: 'DetailList',
  components: { recordDrawer, ProcessPenaltyDialog, FinalDeterminationDialog }, // 注册组件
  props: {
    // 从父组件传来的当前路由/下钻信息
    routeInfo: {
      type: Object,
      default: () => ({ reconciliationPeriod: '', regionName: '', type: '' })
    },
    statusOptions: {
      type: Array,
      default: () => []
    },
    responsibilityTypeList: {
      type: Array,
      default: () => []
    },
    regionsList: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      query: {
        waybillNumber: '', // 运单号/网点/申诉原因搜索
        responsibilityTypeList: [], // 扣罚类型（多选）
        regionId: [], // 大区ID（多选）
        statusList: null, // 处理状态（单选，如 1 待处理, 2 已认罚 等）
        page: 1,
        size: 10
      },
      total: 0,
      selectedRows: [],
      tableData: [],
      // 新增：弹窗控制
      processDialogVisible: false,
      processDialogCount: 0,
      processDialogMode: 'single',
      drawerVisible: false, // 控制抽屉的显示隐藏变量（保留）
      currentRecord: {}, // 当前选中的记录（保留） // 新增：状态统计配置与数据
      statusSummary: {}, // 存储后端返回的统计数据
      statusTabs: [
        { label: this.$t('fixDutyFine.all'), value: null, count: 0, colorClass: '' },
        { label: this.$t('fixDutyFine.pending'), value: 1, count: 0, colorClass: 'orange' },
        { label: this.$t('fixDutyFine.accepted'), value: 2, count: 0, colorClass: 'green' },
        { label: this.$t('fixDutyFine.appealed'), value: 3, count: 0, colorClass: 'red' },
        { label: this.$t('fixDutyFine.exempted'), value: 4, count: 0, colorClass: 'purple' },
        {
          label: this.$t('fixDutyFine.maintainResponsibility'),
          value: 5,
          count: 0,
          colorClass: 'orange'
        }
      ]
    };
  },
  created() {
    // 一进来默认选中的是“全部”状态 Tab
    this.query.statusList = null;
    // 继承父组件下钻时带入的大区（多选）
    this.query.regionId = [...(this.routeInfo.regionIds || [])];
    // 继承父组件下钻时带入的扣罚类型（多选）
    this.query.responsibilityTypeList = [...(this.routeInfo.responsibilityTypeList || [])];
    this.handleQuery();
    this.getStatusCount();
  },
  methods: {
    // 根据当前语言显示大区名称：中文取 zhName，葡语取 puName
    getRegionName(region) {
      if (!region) return '';
      const { zhName, puName, regionName } = region;
      return this.$i18n.locale === 'zh'
        ? zhName || puName || regionName || ''
        : puName || zhName || regionName || '';
    },
    // 后端返回的大区名称（中文或葡语）按当前语言转换
    formatRegionName(name) {
      if (!name) return '';
      const region = this.regionsList.find(
        (item) => item.zhName === name || item.puName === name
      );
      return region ? this.getRegionName(region) : name;
    },
    /**
     * 按钮权限判断（响应式）
     * @param {string} key 权限字段，如 'btn:DeductionConfirmationForm:process'
     * @returns {boolean}
     */
    hasPermit(key) {
      return this.$store.getters.button_permissions.includes(key);
    },
    // 点击顶部“批量最终定责”
    handleBatchFinalDetermination() {
      // 仅「待复核」(status === 3) 的记录参与批量最终定责，其它状态不算选中
      const validRows = this.selectedRows.filter((row) => row.status === 3);
      if (validRows.length === 0) {
        this.$message.warning(this.$t('fixDutyFine.selectPendingReviewRows'));
        return;
      }
      this.$refs.finalDialog.open(
        validRows,
        'batch',
        validRows.length,
        this.routeInfo.reconciliationPeriod
      );
    },

    // 最终定责提交成功后的回调（刷新列表）
    handleFinalSuccess(payload) {
      console.log('最终定责成功，刷新列表', payload);
      this.handleQuery(); // 调用你的列表刷新 API
    },
    handleBack() {
      this.$emit('close'); // 通知父组件关闭并显示汇总页
    },
    // 统一组装查询参数
    buildParams() {
      return {
        reconciliationPeriod: this.routeInfo.reconciliationPeriod || '',
        regionIds: this.query.regionId, // 大区（多选，数组）
        responsibilityTypeList: this.query.responsibilityTypeList, // 扣罚类型（多选，数组）
        statusList: this.query.statusList !== null ? [this.query.statusList] : null, // 后端要求数组
        waybillNumbers: this.query.waybillNumber ? [this.query.waybillNumber] : [] // 搜索运单号
      };
    },
    async handleQuery() {
      const data = {
        pageNumber: this.query.page,
        pageSize: this.query.size,
        params: this.buildParams()
      };
      const res = await getDetailList(data);
      // 赋值给表格数据 (直接使用后端字段)
      this.tableData = res.data.records || [];
      // 更新总数用于分页
      this.total = res.data.total || 0;
    },
    // 明细-状态统计
    async getStatusCount() {
      const data = {
        reconciliationPeriod: this.routeInfo.reconciliationPeriod || '',
        regionIds: this.query.regionId, // 大区（多选，数组）
        responsibilityTypeList: this.query.responsibilityTypeList // 扣罚类型（多选，数组）
      };
      const res = await detailSummary(data);
      if (res.code === 200) {
        this.statusSummary = res.data || {};
        // 映射后端字段到 Tab
        const d = this.statusSummary;
        // 已申诉数(待复核) = total - 待处理 - 已认罚 - 已免责 - 维持定责
        // 或者后端如果有 pendingReviewCount 直接用
        const appealedCount =
          d.total -
          (d.pendingCount || 0) -
          (d.acceptedCount || 0) -
          (d.exemptedCount || 0) -
          (d.maintainedCount || 0);

        this.statusTabs[0].count = d.total || 0;
        this.statusTabs[1].count = d.pendingCount || 0;
        this.statusTabs[2].count = d.acceptedCount || 0;
        this.statusTabs[3].count = d.pendingReviewCount || appealedCount; // 对应申诉状态
        this.statusTabs[4].count = d.exemptedCount || 0;
        this.statusTabs[5].count = d.maintainedCount || 0;
      }
    },
    // 点击 Tab 切换筛选
    handleTabClick(value) {
      if (this.query.statusList !== value) {
        // 改成 statusList
        this.query.statusList = value; // 改成 statusList
        this.query.page = 1;
        this.handleQuery();
      }
    },
    // 导出时也复用 buildParams，保持一致
    exportData() {
      const data = this.buildParams();
      getDetailListExport(data).then((res) => {
        if (res.code === 200) {
          goRWExportList(this);
        }
      });
    },
    // 重置
    resetQuery() {
      this.query = {
        waybillNumber: '',
        responsibilityTypeList: [...(this.routeInfo.responsibilityTypeList || [])],
        regionId: [...(this.routeInfo.regionIds || [])],
        statusList: null,
        page: 1,
        size: 10
      };
      this.handleQuery();
    },
    handleSelectionChange(selection) {
      this.selectedRows = selection;
    },
    // 1. 查看操作：打开抽屉
    handleAction(row, type) {
      this.currentRecord = row;
      if (type === 'view') {
        this.$refs.drawer.open(row);
      } else if (type === 'process') {
        this.$refs.processDialog.open(row, 'single', 1);
      } else if (type === 'final') {
        // 新增：调用最终定责弹窗
        this.$refs.finalDialog.open(row, 'single', 1);
      } else {
        console.log(row, 'handleAction');
      }
    },
    // 2. 批量处理：打开弹窗
    handleBatchProcess() {
      // 仅「待处理」(status === 1) 的记录参与批量处理，其它状态不算选中
      const validRows = this.selectedRows.filter((row) => row.status === 1);
      if (validRows.length === 0) {
        this.$message.warning(this.$t('fixDutyFine.selectPendingRows'));
        return;
      }
      // 传入有效的选中数组（弹窗内按数组长度显示选中票数）
      this.$refs.processDialog.open(validRows, 'batch', validRows.length);
    },
    // 4. 处理提交
    // 成功的回调（用于刷新列表）
    handleProcessSuccess(payload) {
      console.log('处理成功，刷新列表', payload);
      this.handleQuery(); // 刷新你的列表接口
    },
    // 格式化数字（千分位）
    formatNumber(num) {
      if (num === null || num === undefined) return '--';
      return Number(num)
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    },

    // 格式化金额
    formatCurrency(num) {
      if (num === null || num === undefined) return '--';
      return 'R$ ' + Number(num).toFixed(2);
    },
    getStatusClass(status) {
      if (status === 1) return 'status-orange'; // 待处理
      if (status === 2) return 'status-green'; // 已认罚
      if (status === 3) return 'status-orange'; // 已申诉待复核 (图片里也是橙色)
      if (status === 4) return 'status-purple'; // 已免责
      if (status === 5) return 'status-red'; // 维持原定责
      return 'status-default';
    },
    // 页码发生变化时
    getCurrentTableData(page) {
      this.query.page = page;
      this.handleQuery();
    },

    // 每页条数发生变化时
    getSizeTableData(size) {
      this.query.size = size;
      this.query.page = 1; // 切换每页条数时重置到第一页
      this.handleQuery();
    }
  }
};
</script>

<style lang="scss" scoped>
.detail-container {
  background: #fff;
  padding: 20px;
  border-radius: 4px;
}

.detail-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;

  .page-title {
    font-size: 20px;
    font-weight: bold;
    margin: 10px 0 5px;
    color: #303133;
  }

  .sub-title {
    font-size: 12px;
    color: #909399;
  }
}

.status-tabs {
  display: flex;
  border-bottom: 1px solid #ebeef5;
  margin-bottom: 20px;

  .tab-item {
    padding: 10px 20px;
    cursor: pointer;
    border-bottom: 2px solid transparent;
    color: #606266;
    font-size: 14px;

    &.active {
      color: #08a65a;
      border-bottom-color: #08a65a;
      font-weight: bold;
    }

    .count {
      margin-left: 5px;
      font-weight: normal;
      font-size: 12px;
    }

    .count.orange {
      color: #e6a23c;
    }

    .count.green {
      color: #67c23a;
    }

    .count.red {
      color: #f56c6c;
    }

    .count.purple {
      color: #9254de;
    }
  }
}

.filter-area {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 20px;

  .batch-actions {
    display: flex;
    align-items: center;

    .hint-text {
      font-size: 12px;
      color: #909399;
      margin-right: 15px;
      width: 350px;
      text-align: right;
    }
  }
}

.pagination-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;

  .pagination-left {
    font-size: 12px;
    color: #909399;
  }
}

.text-orange {
  color: #e6a23c;
}

.text-green {
  color: #67c23a;
}

.text-red {
  color: #f56c6c;
}

.text-purple {
  color: #9254de;
}

/* 运单号加粗 */
.bold-waybill {
  font-weight: bold;
  color: #303133; /* 保持深色 */
}

/* 运单号可点击查看 */
.clickable-waybill {
  cursor: pointer;
  transition: color 0.2s;

  &:hover {
    color: #00a551;
    text-decoration: underline;
  }
}

/* 状态胶囊通用样式 */
.status-pill {
  display: inline-block;
  font-size: 12px;
  font-weight: bold;
  padding: 4px 10px;
  border-radius: 15px; /* 胶囊圆角 */
  line-height: 1.2;
  white-space: normal; /* 允许换行，比如"已申诉待复核" */
  text-align: center;
  min-width: 50px;
  word-break: break-all;
}

/* 待处理 (橙色) */
.status-orange {
  background-color: #fdf6ec; /* 浅橙色背景 */
  color: #e6a23c; /* 橙色文字 */
}

/* 已认罚 (绿色) */
.status-green {
  background-color: #f0f9eb; /* 浅绿色背景 */
  color: #67c23a; /* 绿色文字 */
}

/* 已免责 (紫色) */
.status-purple {
  background-color: #f9f0ff; /* 浅紫色背景 */
  color: #9254de; /* 紫色文字 */
}

/* 维持原定责 (红色) */
.status-red {
  background-color: #fef0f0; /* 浅红色背景 */
  color: #f56c6c; /* 红色文字 */
}

.status-default {
  background-color: #f4f4f5;
  color: #909399;
}
</style>
